import { useOutletContext } from 'react-router-dom';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TaskCard from '../../../../components/TaskCard/TaskCard';
import {
  downloadFile,
  fetchPagedTasks,
  getAllTaskByIds,
} from '../../../../services/TaskService';
import { getCurrentUser } from '../../../../services/authService';
import { useTaskHtml } from 'hooks/task/useTaskHtml';
import useViewportHeight from 'utils/useViewportHeight';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { configOldMovementsI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';
import ApprovalFlowModal from '../../../ApprovalRequest/ApprovalFlowModal';
import useTaskWorkflowFlow from '../../../../hooks/useTaskWorkflowFlow';
import FloatingAlert from 'components/Alert/FloatingAlert';

const CaseOldMovements = () => {
  const { language } = useLanguage();
  const configOldMovementsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configOldMovementsI18n });
  const { showSnackbar } = useSnackbar();
  const { DesignSystemComponents, caseData } = useOutletContext();
  const { Dropdown, Input } = DesignSystemComponents;

  const location = useLocation();
  const taskIdToOpen = location.state?.taskIdToOpen || null;
  const taskRefs = useRef({});

  const [userId, setUserId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [selectedTaskForAttachment, setSelectedTaskForAttachment] =
    useState(null);
  const isOld = true;
  const {
    workflowByTaskId,
    workflowError,
    isWorkflowFlowLoading,
    selectedWorkflowFlow,
    selectedWorkflowRequest,
    hydrateTaskWorkflows,
    openTaskWorkflowFlow,
    closeTaskWorkflowFlow,
    clearWorkflowError,
  } = useTaskWorkflowFlow();

  const { getTaskHtml } = useTaskHtml();

  useEffect(() => {
    configOldMovementsI18n.changeLanguage(configOldMovementsLanguage);
  }, [configOldMovementsLanguage]);

  const handleOpenAttachment = async (task) => {
    setSelectedTaskForAttachment(task);
    setShowAttachmentModal(true);
    await getTaskHtml(task.id);
  };

  const handleOpenCommentDocument = async (doc) => {
    const payload = {
      tipoComentario: 1,
      commentableId: doc.id,
      urlAttachment: doc.urlAttachment || doc.urlAdjunto,
      nameAttachment: doc.nameAttachment || doc.nombreAdjunto,
    };
    try {
      await downloadFile(payload);
    } catch (e) {
      console.error('Error al descargar archivo: ', e);
      showSnackbar(t('s3_download_error'), 'error');
    }
  };

  const fetchTasksDetails = async (tasksIds) => {
    try {
      const resp = await getAllTaskByIds(tasksIds, isOld);
      const items = Array.isArray(resp) ? resp : [];
      setTasks(items);
      await hydrateTaskWorkflows(items);
    } catch (error) {
      console.error(error);
      setTasks([]);
      await hydrateTaskWorkflows([]);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoadingTasks(true);
      if (!caseData?.id) return;
      const queryParams = { casoId: caseData.id, pageSize: 100 };
      const resp = await fetchPagedTasks(queryParams);
      const taskIds = resp.items.filter((t) => t.id).map((t) => t.id);
      await fetchTasksDetails(taskIds);
    } catch (error) {
      console.error('Error al obtener Tareas:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    } else {
      console.warn('No se pudo obtener el usuario autenticado');
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      await fetchTasks();
    };
    fetchData();
  }, [caseData?.id, userId]);

  const statusOptions = useMemo(
    () => [
      { label: t('configOldMovements_filter_all'), value: 'todos' },
      {
        label: t('configOldMovements_filter_inProgress'),
        value: 'en progreso',
      },
      { label: t('configOldMovements_filter_approved'), value: 'aprobada' },
      { label: t('configOldMovements_filter_rejected'), value: 'rechazada' },
      {
        label: t('configOldMovements_filter_unassigned'),
        value: 'sin asignar',
      },
    ],
    [t]
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredTasks = (tasks || [])
    .filter((task) => {
      const title = (task.titulo || task.title || '').toString().toLowerCase();
      const description = (task.descripcion || task.description || '')
        .toString()
        .toLowerCase();

      const matchesSearch =
        !normalizedQuery ||
        title.includes(normalizedQuery) ||
        description.includes(normalizedQuery);

      const rawEstado = (task.estado || '').toLowerCase();
      let canonicalEstado = '';

      if (['in_progress', 'en progreso'].includes(rawEstado)) {
        canonicalEstado = 'en progreso';
      } else if (['approved', 'aprobada'].includes(rawEstado)) {
        canonicalEstado = 'aprobada';
      } else if (['rejected', 'rechazada'].includes(rawEstado)) {
        canonicalEstado = 'rechazada';
      } else if (['unassigned', 'sin asignar'].includes(rawEstado)) {
        canonicalEstado = 'sin asignar';
      } else {
        canonicalEstado = rawEstado;
      }

      const matchesStatus =
        statusFilter === 'todos' || canonicalEstado === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .slice()
    .sort((a, b) => {
      const da = a?.fechaCreacion ? Date.parse(a.fechaCreacion) : 0;
      const db = b?.fechaCreacion ? Date.parse(b.fechaCreacion) : 0;
      return db - da;
    });

  // Scroll a la tarea que vino desde navigate()
  useEffect(() => {
    if (!taskIdToOpen) return;

    // Esperar al render (importantísimo)
    setTimeout(() => {
      const element = taskRefs.current[taskIdToOpen];

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // Opcional: resaltar la tarjeta
        element.classList.add('highlight-task');
        setTimeout(() => element.classList.remove('highlight-task'), 2000);
      }
    }, 200); // pequeño delay para asegurar render
  }, [tasks, filteredTasks]);

  const viewportHeight = useViewportHeight();

  let heightPercent = 60;

  if (viewportHeight < 600) {
    // Zoom 150%
    heightPercent = 80;
  } else if (viewportHeight < 700) {
    // Zoom 125%
    heightPercent = 85;
  } else if (viewportHeight < 800) {
    // Zoom 110%
    heightPercent = 90;
  } else if (viewportHeight < 900) {
    // Zoom 100%
    heightPercent = 94;
  } else if (viewportHeight < 1000) {
    // Zoom 90%
    heightPercent = 94;
  } else if (viewportHeight < 1100) {
    // Zoom 80%
    heightPercent = 96;
  }

  return (
    <div
      className='my-4 case-old-movements-tab'
      style={{
        height: `${heightPercent}%`,
      }}
    >
      <div className='px-4'>
        {loadingTasks && (
          <p className='font-light m-2'>{t('configOldMovements_loading')}</p>
        )}

        {tasks?.length === 0 && !loadingTasks && (
          <p className='font-light text-center mt-4'>
            {t('configOldMovements_empty')}
          </p>
        )}
        {tasks?.length > 0 && !loadingTasks && (
          <>
            <div className='row'>
              <div className='col-12 col-md-6'>
                <Input
                  icon='search'
                  placeholder={t('configOldMovements_searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className='col-12 col-md-3'>
                <Dropdown
                  placeholder={t('configOldMovements_statusPlaceholder')}
                  options={statusOptions}
                  value={
                    statusOptions.find((opt) => opt.value === statusFilter) ||
                    statusOptions[0]
                  }
                  onChange={(opt) => setStatusFilter(opt?.value || 'todos')}
                />
              </div>
            </div>
          </>
        )}

        {tasks?.length > 0 && filteredTasks.length === 0 && (
          <p className='font-light m-2'>{t('configOldMovements_noMatches')}</p>
        )}
      </div>

      {workflowError && (
        <FloatingAlert
          message={workflowError}
          type='danger'
          onClose={clearWorkflowError}
        />
      )}
      <div className='activity-inner-scroll px-4 case-old-movements-list'>
        {filteredTasks.map((task) => (
          <TaskCard
            key={task.id}
            ref={(el) => (taskRefs.current[task.id] = el)}
            DesignSystemComponents={DesignSystemComponents}
            task={task}
            onOpenAttachment={handleOpenAttachment}
            onOpenCommentDocument={handleOpenCommentDocument}
            workflowInstance={workflowByTaskId[task.id] || null}
            onViewWorkflow={() => openTaskWorkflowFlow(task)}
            isWorkflowFlowLoading={isWorkflowFlowLoading}
          />
        ))}
      </div>
      <ApprovalFlowModal
        DesignSystemComponents={DesignSystemComponents}
        show={!!selectedWorkflowFlow}
        flow={selectedWorkflowFlow}
        request={selectedWorkflowRequest}
        onClose={() => {
          closeTaskWorkflowFlow();
          clearWorkflowError();
        }}
      />
    </div>
  );
};

export default CaseOldMovements;
